// src/pages/itsm/PurchaseOrders.tsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Sun, Moon } from 'lucide-react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Pill, StatTile, EmptyState } from '../../components/ui/UI'

const STATUS_CONFIG: Record<string, { label: string; tone: 'neutral' | 'violet' | 'success' | 'danger' | 'info' }> = {
    draft: { label: 'Draft', tone: 'neutral' },
    pending_approval: { label: 'Pending Approval', tone: 'violet' },
    approved: { label: 'Approved', tone: 'success' },
    rejected: { label: 'Rejected', tone: 'danger' },
    ordered: { label: 'Ordered', tone: 'info' },
    received: { label: 'Received', tone: 'success' },
    closed: { label: 'Closed', tone: 'neutral' },
}

export default function PurchaseOrders() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [pos, setPOs] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState('all')

    useEffect(() => { fetchPOs() }, [statusFilter])

    const fetchPOs = async () => {
        let query = supabase
            .from('itsm_purchase_orders')
            .select(`*, itsm_vendors(name), itsm_tickets(ticket_number, title), itsm_agents!requested_by(name)`)
            .order('created_at', { ascending: false })

        if (statusFilter !== 'all') query = query.eq('status', statusFilter)
        const { data } = await query
        setPOs(data || [])
        setLoading(false)
    }

    const s = styles

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <button onClick={() => navigate('/dashboard/itsm')} style={s.back}>
                <ArrowLeft size={13} /> Back to ITSM
            </button>
            <div style={s.header}>
                <div>
                    <h1 style={s.title}>Purchase Orders</h1>
                    <p style={s.subtitle}>{pos.length} purchase orders</p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                        onClick={toggleTheme}
                        aria-label="Toggle dark mode"
                        style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: `1px solid ${T.borderStrong}`, background: T.surface, color: T.text2, cursor: 'pointer' }}
                    >
                        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                    </button>
                    <button style={s.btnPrimary} onClick={() => navigate('/dashboard/itsm/po/new')}>+ New PO</button>
                </div>
            </div>

            {/* Stats */}
            <Card style={{ padding: 0, display: 'flex', overflow: 'hidden', marginBottom: 20 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <StatTile label="Draft" value={pos.filter(p => p.status === 'draft').length} tone="accent" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <StatTile label="Pending Approval" value={pos.filter(p => p.status === 'pending_approval').length} tone="violet" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <StatTile label="Approved" value={pos.filter(p => p.status === 'approved').length} tone="success" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <StatTile label="Total Value" value={`SAR ${pos.reduce((s, p) => s + (p.total_amount || 0), 0).toLocaleString()}`} tone="info" />
                </div>
            </Card>

            {/* Filters */}
            <div style={{ marginBottom: 16 }}>
                <select style={s.select}
                    value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="all">All Status</option>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            <Card style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                            {['PO Number', 'Title', 'Vendor', 'Ticket', 'Requested By', 'Total', 'Status', 'Date'].map(h => (
                                <th key={h} style={s.th}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pos.map(po => {
                            const status = STATUS_CONFIG[po.status] || STATUS_CONFIG.draft
                            return (
                                <tr key={po.id} style={s.tr} onClick={() => navigate(`/dashboard/itsm/po/${po.id}`)}>
                                    <td style={s.td}><span className="jr-mono" style={{ fontSize: 12, color: T.text3, fontWeight: 600 }}>{po.po_number}</span></td>
                                    <td style={s.td}><span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{po.title}</span></td>
                                    <td style={s.td}><span style={{ fontSize: 12, color: T.text2 }}>{po.itsm_vendors?.name || '—'}</span></td>
                                    <td style={s.td}>
                                        {po.itsm_tickets ? (
                                            <span style={{ fontSize: 11, color: T.info, fontWeight: 600 }}>{po.itsm_tickets.ticket_number}</span>
                                        ) : '—'}
                                    </td>
                                    <td style={s.td}><span style={{ fontSize: 12, color: T.text2 }}>{po.itsm_agents?.name || '—'}</span></td>
                                    <td style={s.td}>
                                        <span className="jr-mono" style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                                            SAR {(po.total_amount || 0).toLocaleString()}
                                        </span>
                                    </td>
                                    <td style={s.td}>
                                        <Pill tone={status.tone}>{status.label}</Pill>
                                    </td>
                                    <td style={s.td}><span style={{ fontSize: 11, color: T.text3 }}>{new Date(po.created_at).toLocaleDateString('en-GB')}</span></td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
                {!loading && pos.length === 0 && <EmptyState title="No purchase orders found" />}
            </Card>
        </div>
    )
}

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1200, margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    back: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: T.info, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    subtitle: { fontSize: 13, color: T.text2 },
    select: { height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, outline: 'none', background: T.surface, color: T.text },
    th: { fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' as const },
    td: { fontSize: 13, padding: '10px 12px', borderBottom: `1px solid ${T.border}` },
    tr: { cursor: 'pointer' },
    btnPrimary: { height: 36, padding: '0 18px', background: T.accent, color: T.accentFg, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
}
